// src/pages/Service/ServiceReturn/ServiceReturnVoucherBody.js
// Layout follows ServiceVoucherBody (Service/Service/ServiceVoucher).
import React from "react";
import { Row, Col, Table, Typography, Divider } from "antd";
import { useSelector } from "react-redux";
import { convertNumberToWords, formatToCompanyTimezone, numberFormat, numberFormatTax } from "../../../lib/functions";
import { selectAuthInfo } from "../../Auth/authSelectors";
import "../Service/ServiceVoucher/components/voucher-mobile.css";

const { Title, Text } = Typography;

const ServiceReturnVoucherBody = ({ data, institution, showStamp = false, showSignature = false }) => {
  const authInfo = useSelector(selectAuthInfo);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";

  if (!data) return null;

  const details = data.details || [];
  const payments = data.payments || [];
  const total = Number(data.total_amount || 0);
  const refund = Number(data.paid_amount || 0);

  const columns = [
    { title: "NO.", render: (_, __, index) => index + 1, width: 40 },
    {
      title: "Service",
      render: (_, record) => (
        <>
          <div>{record.item_name}</div>
          {record.item_description && (
            <div style={{ fontSize: 11, color: "#666", marginTop: 4, whiteSpace: "pre-wrap" }}>
              {record.item_description}
            </div>
          )}
        </>
      ),
    },
    { title: "Rate", dataIndex: "service_r_rate", align: "right", render: (v) => Number(v).toFixed(2), width: 70 },
    { title: "Returned Qty", dataIndex: "service_r_qty", align: "right", width: 100 },
    { title: "Discount", dataIndex: "item_discount", align: "right", render: (v) => Number(v).toFixed(2), width: 90 },
    { title: taxLabel, dataIndex: "item_tax", align: "right", render: (v) => Number(v).toFixed(2), width: 80 },
    { title: "Total", dataIndex: "item_total", align: "right", render: (v) => Number(v).toFixed(2), width: 90 },
  ];

  const line = (label, value, strong = false, border = false) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        ...(border ? { borderTop: "1px solid #000", paddingTop: 4, marginTop: 4 } : {}),
      }}
    >
      <Text strong={strong}>{label}</Text>
      <Text strong={strong}>{value}</Text>
    </div>
  );

  return (
    <div data-testid="service-return-voucher-card" className="voucher-section invoice-v2">
      <Row justify="space-between" align="top" className="invoice-header">
        <Col flex="0 0 320px" className="bill-to" style={{ marginTop: "58px" }}>
          <Text strong>Customer/ Debtor Code: &nbsp;{data.acc_code}</Text>
          <br />
          <Text strong>Name: &nbsp;{data.acc_name}</Text>
          <br />
          <Text className="wrap-text">Address: &nbsp; {data.address}</Text>
          {data.contact_no && <Text>Contact No: &nbsp; +{data.contact_no}</Text>}
        </Col>
        <Col flex="320px" className="invoice-title">
          <Title level={2}>SERVICE RETURN</Title>
          <Text strong>Voucher No: {data.service_r_voucher_no}</Text>
          <br />
          <Text>Return Date: {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}</Text>
          <br />
          <Text>Against Service: {data.service_voucher_no}</Text>
          <br />
          <Text>Returned By: {data.full_name?.split(" ")[0]}</Text>
          <br />
          <Text className="wrap-text">Address : {institution?.street_address}</Text>
          {institution?.phone_no && <Text>Contact No : &nbsp; +{institution?.phone_no}</Text>}
          <br />
          {institution?.vat_license && (
            <Text>
              {institution?.country?.trim().toLowerCase() === "united arab emirates" ? "TRN No: " : "Tax Reg No: "}
              {institution?.vat_license}
            </Text>
          )}
        </Col>
      </Row>

      <Table
        className="invoice-items"
        dataSource={details}
        columns={columns}
        pagination={false}
        rowKey="service_r_d_id"
        size="small"
      />

      <Divider />
      <Row>
        <Col span={16}>
          {payments.length > 0 && (
            <>
              <Text strong>Refund paid from</Text>
              {payments.map((p) => (
                <div key={p.tran_id}>
                  <Text>
                    {p.from_acc_name}: {numberFormat(p.tran_amount)}
                  </Text>
                </div>
              ))}
            </>
          )}
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          {line("Sub Total :", numberFormat(data.sub_total || 0))}
          {line("Discount :", numberFormat(data.total_discount || 0))}
          {isIndia && data.place_of_supply_type === "intra" && (
            <>
              {line("CGST :", numberFormatTax(data.cgst_amount))}
              {line("SGST :", numberFormatTax(data.sgst_amount))}
            </>
          )}
          {isIndia && data.place_of_supply_type === "inter" && line("IGST :", numberFormatTax(data.igst_amount))}
          {!isIndia && line(`${taxLabel} :`, numberFormat(data.total_tax || 0))}
          {line(`Return Total (${institution?.currency || ""}): `, numberFormat(total), true, true)}
          {line(`Refunded (${institution?.currency || ""}): `, numberFormat(refund), true, true)}
          {line(`Credited to Customer (${institution?.currency || ""}): `, numberFormat(total - refund), true)}
        </Col>
      </Row>

      <Divider />
      <Row>
        <Col span={24}>
          <Text>In Word of Return Total : {convertNumberToWords(total, institution?.currency)}</Text>
          <br />
          <Text>Description : {data.narration || "-"}</Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}
            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReturnVoucherBody;
